import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { messages } from "./messages";
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  type Language,
  type TranslationKey,
} from "./types";

const STORAGE_KEY = "casinomenu.lang";

type I18nContextValue = {
  lang: Language;
  setLang: (next: Language) => void;
  t: (key: TranslationKey) => string;
  prefix: string;
  swapLanguageInPath: (next: Language) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function detectInitialLanguage(pathname: string): Language {
  const fromPath = pathnameToLanguage(pathname);
  if (fromPath) return fromPath;

  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored && LANGUAGES.includes(stored)) return stored;
  } catch {
    // ignore
  }

  const navLang =
    window.navigator.language || window.navigator.languages?.[0] || "";
  if (navLang.toLowerCase().startsWith("sv")) return "sv";
  if (navLang.toLowerCase().startsWith("en")) return "en";

  return DEFAULT_LANGUAGE;
}

export function pathnameToLanguage(pathname: string): Language | null {
  const segs = pathname.split("/").filter(Boolean);
  const first = segs[0];
  if (first === "sv" || first === "en") return first;
  return null;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [lang, setLangState] = useState<Language>(() =>
    detectInitialLanguage(location.pathname),
  );

  const setLang = useCallback(
    (next: Language) => {
      setLangState(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
      try {
        document.documentElement.setAttribute("lang", next);
      } catch {
        // ignore
      }
    },
    [],
  );

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  // Soft redirect: if URL has no language prefix, redirect to detected language.
  useEffect(() => {
    const fromPath = pathnameToLanguage(location.pathname);
    if (!fromPath) {
      const target = `/${lang}${location.pathname === "/" ? "" : location.pathname}${location.search}`;
      navigate(target, { replace: true });
    } else if (fromPath !== lang) {
      // URL is the source of truth — sync state to URL.
      setLangState(fromPath);
    }
  }, [location.pathname, location.search, lang, navigate]);

  const t = useCallback(
    (key: TranslationKey) => messages[lang][key] ?? messages[DEFAULT_LANGUAGE][key] ?? key,
    [lang],
  );

  const prefix = `/${lang}`;

  const swapLanguageInPath = useCallback(
    (next: Language) => {
      const segs = location.pathname.split("/").filter(Boolean);
      if (segs[0] === "sv" || segs[0] === "en") {
        segs[0] = next;
      } else {
        segs.unshift(next);
      }
      return "/" + segs.join("/") + location.search;
    },
    [location.pathname, location.search],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ lang, setLang, t, prefix, swapLanguageInPath }),
    [lang, setLang, t, prefix, swapLanguageInPath],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
